import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { backendService } from "../../../services/backend.service";

interface UpdateAgencyPostDelayParams {
	requestId: string;
	agencyPostDelayHours: number;
}

/**
 * Re-times a pending agency post (`PUT /staffing/request-agency-post-delay/{id}`). Only saves the
 * new number — if that puts the send time in the past, the background task sends it on its next run.
 */
export const useUpdateAgencyPostDelay = () => {
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { refreshRequests } = useRequestsContext();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: ({ requestId, agencyPostDelayHours }: UpdateAgencyPostDelayParams) =>
			backendService.updateAgencyPostDelay(requestId, chosenDepartment.department_id, agencyPostDelayHours),
		onSuccess: async (_response, { requestId, agencyPostDelayHours }) => {
			track("update_agency_post_delay_success", {
				request_id: requestId,
				department_id: chosenDepartment.department_id,
				delay_hours: agencyPostDelayHours,
			});
			await refreshRequests();
		},
		onError: (error: Error, { requestId }) => {
			trackError("update_agency_post_delay_error", { request_id: requestId, department_id: chosenDepartment.department_id });
			showErrorSnackbar(t("error.updating.agency.post.delay"), {}, error);
		},
	});
};
